import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Icon } from '../design/Icon';
import { fixtures } from '../domain/puzzle/fixtures';
import { PuzzleSessionController, type Session } from '../domain/puzzle/PuzzleSessionController';
import { PuzzleOrchestrator } from '../domain/puzzle/PuzzleOrchestrator';
import { BoardAdapter } from '../features/board/BoardAdapter';
import { warmBoardImages } from '../features/board/pieces';
import { Sheet } from '../components/Sheet';
import { TurnPrompt } from '../components/TurnPrompt';
import { BoardSkeleton } from '../components/BoardSkeleton';
import { PracticeSetup } from '../components/PracticeSetup';
import { StatsPage } from '../components/StatsPage';
import { MoveFeedback, type MoveCue } from '../components/MoveFeedback';
import { motionDuration } from '../components/motionDuration';
import { LastMove } from '../components/LastMove';
import { PuzzleFacts } from '../components/PuzzleFacts';
import { puzzleThemes } from '../components/puzzleThemes';
import { Scoresheet, useScoresheet } from '../components/Scoresheet';
import { SessionStrip } from '../components/SessionStrip';
import { useSessionStats } from '../components/useSessionStats';
import { TodayLine } from '../components/TodayLine';
import { adaptiveRange } from '../domain/puzzle/adaptive';
import { PersonalStore, type Confidence } from '../infrastructure/indexeddb/PersonalStore';
import { PuzzleQueue } from '../infrastructure/api/PuzzleQueue';
import { defaults, readSettings, saveSettings, type Settings } from '../features/settings/settings';
import { themeLabel } from '../domain/puzzle/catalog';
import type { PuzzleDefinition } from '../domain/puzzle/PuzzleDefinition';
import { AudioService } from '../infrastructure/audio/AudioService';
import { moveSound } from '../domain/chess/audioEvent';
import { EngineCoordinator } from '../infrastructure/engine/EngineCoordinator';
import { Haptics } from '../infrastructure/haptics/Haptics';
/** Long enough to see the final move and the solve summary. */
const AUTO_NEXT_MS = 1600;
const sheetNames = new Set(['review', 'settings', 'filters', 'details', 'licenses', 'reset']);
const currentSheet = () => {
  const name = location.hash.slice(1);
  return sheetNames.has(name) ? name : undefined;
};
/** Only fields written into personal history; board animation/refutation frames are transient. */
const progressSignature = (s: Session) =>
  JSON.stringify([
    s.attemptId ?? s.startedAt,
    s.solved,
    s.revealed,
    s.completedAt,
    s.outcome,
    s.hintsUsed,
    s.mistakes,
    s.continuation,
  ]);
export function App() {
  const [controller] = useState(() => new PuzzleSessionController(fixtures[0]));
  const state = useSyncExternalStore(controller.subscribe, () => controller.snapshot),
    s = state.session,
    rules = controller.rules;
  const [settings, setSettings] = useState(readSettings),
    [notice, setNotice] = useState(''),
    [store] = useState(() => new PersonalStore(setNotice));
  const {
    stats,
    recent,
    allTime,
    review,
    strength,
    history,
    refresh: refreshStats,
  } = useSessionStats(store);
  const scoreMoves = useScoresheet(s);
  const visibleThemes = puzzleThemes(s.puzzle.themes);
  const notebookQuery =
    '(min-width: 1024px) and (orientation: landscape), (orientation: landscape) and (max-height: 540px) and (min-width: 520px)';
  const [notebook, setNotebook] = useState(() => matchMedia(notebookQuery).matches);
  useEffect(() => {
    const media = matchMedia(notebookQuery);
    const change = () => setNotebook(media.matches);
    media.addEventListener('change', change);
    return () => media.removeEventListener('change', change);
  }, [notebookQuery]);
  const [filterDraft, setFilterDraft] = useState(settings);
  const [practiceStep, setPracticeStep] = useState<'difficulty' | 'theme'>('difficulty');
  const [home, setHome] = useState(true);
  const [statsOpen, setStatsOpen] = useState(() => location.hash === '#stats');
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [home, statsOpen, practiceStep]);
  const sessionMode = useRef<string>(settings.mode);
  const savedProgress = useRef<string | undefined>(undefined);
  const [, refreshQueue] = useState(0),
    [queue] = useState(
      () =>
        new PuzzleQueue(
          store,
          settings,
          undefined,
          new URLSearchParams(location.search).get('source') === 'fixtures',
          () => refreshQueue((n) => n + 1),
        ),
    );
  // R5: highlights and arrows on the board, and a counter that clears them all.
  const [annotations, setAnnotations] = useState(0);
  const [clearToken, setClearToken] = useState(0);
  // R5: the Pen tool: while on, taps and drags on the board draw instead of moving.
  const [pen, setPen] = useState(false);
  const [ready, setReady] = useState(false),
    [flipped, setFlipped] = useState(false),
    [sheet, setSheet] = useState<string | undefined>(currentSheet);
  const navigation = useRef(0);
  const exactAbort = useRef<AbortController | undefined>(undefined);
  const [exactLoad, setExactLoad] = useState<{ id: string; failed: boolean }>();
  const beginNavigation = () => {
    exactAbort.current?.abort();
    setExactLoad(undefined);
    return ++navigation.current;
  };
  useEffect(() => () => exactAbort.current?.abort(), []);
  useEffect(() => {
    if (!statsOpen) return;
    void refreshStats();
  }, [statsOpen, refreshStats]);
  const [systemReduced, setSystemReduced] = useState(
    () => matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  const reduced = settings.reduced || systemReduced;
  const [moveCue, setMoveCue] = useState<MoveCue>();
  const cueId = useRef(0);
  useEffect(() => {
    if (!moveCue) return;
    const duration = motionDuration(document.documentElement);
    const timer = window.setTimeout(() => setMoveCue(undefined), duration);
    return () => window.clearTimeout(timer);
  }, [moveCue]);
  const nextButton = useRef<HTMLButtonElement>(null);
  const focusedSolve = useRef<number>(undefined);
  useEffect(() => {
    if (!s.solved || s.continuation || focusedSolve.current === s.generation) return;
    focusedSolve.current = s.generation;
    if (ready && !home && !statsOpen && !sheet) nextButton.current?.focus({ preventScroll: true });
  }, [s.solved, s.continuation, s.generation, ready, home, statsOpen, sheet]);
  const [audio] = useState(() => new AudioService()),
    engine = useRef<EngineCoordinator | undefined>(undefined);
  useEffect(() => {
    // Warm the board and pieces while home is showing (cold-cache first paint).
    const timer = window.setTimeout(() => void Promise.all(warmBoardImages()), 250);
    return () => window.clearTimeout(timer);
  }, []);
  const [haptics] = useState(() => new Haptics());
  useEffect(() => {
    haptics.enabled = settings.haptics;
  }, [haptics, settings.haptics]);
  useEffect(() => () => haptics.dispose(), [haptics]);
  const feedbackSounds = useRef(settings.feedbackSounds);
  useEffect(() => {
    feedbackSounds.current = settings.feedbackSounds;
  }, [settings.feedbackSounds]);
  const [soundTest, setSoundTest] = useState('');
  const [hasMoved, setHasMoved] = useState(false);
  const madeMove = useRef(false);
  useEffect(() => {
    const coordinator = new EngineCoordinator(controller);
    engine.current = coordinator;
    return () => coordinator.dispose();
  }, [controller]);
  useEffect(() => {
    if (engine.current) engine.current.profile = settings.engineProfile;
  }, [settings.engineProfile]);
  useEffect(() => {
    audio.settings(settings.muted, settings.volume, settings.overOtherAudio);
  }, [audio, settings.muted, settings.volume, settings.overOtherAudio]);
  useEffect(() => {
    return () => audio.dispose();
  }, [audio]);
  useEffect(() => {
    let previous = controller.snapshot;
    let cue: ReturnType<typeof setTimeout> | undefined;
    const unsubscribe = controller.subscribe(() => {
      const current = controller.snapshot;
      if (current.session.generation === previous.session.generation) {
        if (
          madeMove.current &&
          current.session.lastMove !== previous.session.lastMove &&
          current.session.lastMove
        )
          audio.play(moveSound(current.session.lastMove));
        // Optional right/wrong cue, just after the board sound so both stay audible.
        const feedback =
          current.status === 'incorrect' && previous.status === 'user-turn'
            ? 'incorrect'
            : current.status === 'solved' && previous.status !== 'solved'
              ? 'solved'
              : undefined;
        if (feedback && madeMove.current && feedbackSounds.current) {
          const generation = current.session.generation;
          clearTimeout(cue);
          cue = setTimeout(() => {
            if (controller.snapshot.session.generation === generation) audio.play(feedback);
          }, 140);
        }
      } else {
        clearTimeout(cue);
        audio.cancelPending();
      }
      previous = current;
    });
    return () => {
      clearTimeout(cue);
      unsubscribe();
    };
  }, [audio, controller]);
  useEffect(() => {
    const m = matchMedia('(prefers-reduced-motion: reduce)'),
      change = () => setSystemReduced(m.matches);
    m.addEventListener('change', change);
    return () => m.removeEventListener('change', change);
  }, []);
  useEffect(() => {
    return () => queue.dispose();
  }, [queue]);
  useEffect(() => {
    const o = new PuzzleOrchestrator(controller, reduced);
    return () => o.dispose();
  }, [controller, reduced]);
  useEffect(() => {
    saveSettings(settings);
  }, [settings]);
  useEffect(() => {
    if (!ready || (!s.solved && !s.mistakes.length && !s.hintsUsed)) return;
    const signature = progressSignature(s);
    if (savedProgress.current === signature) return;
    savedProgress.current = signature;
    void store.save(s, sessionMode.current).then(refreshStats);
  }, [s, ready, store, refreshStats]);
  useEffect(() => {
    const back = () => {
      const hash = location.hash.slice(1);
      setStatsOpen(hash === 'stats');
      setSheet(currentSheet());
    };
    window.addEventListener('popstate', back);
    window.addEventListener('hashchange', back);
    return () => {
      window.removeEventListener('popstate', back);
      window.removeEventListener('hashchange', back);
    };
  }, []);
  const openSheet = (name: string) => {
    if (name === 'filters') {
      setFilterDraft(settings);
      setPracticeStep('difficulty');
    }
    window.history.pushState({ sheet: name }, '', `#${name}`);
    setSheet(name);
  };
  const replaceSheet = (name: string) => {
    window.history.replaceState(window.history.state, '', `#${name}`);
    setSheet(name);
  };
  const closeSheet = () => {
    setSheet(undefined);
    if (currentSheet()) {
      if (window.history.state?.sheet) window.history.back();
      else window.history.replaceState(null, '', location.pathname + location.search);
    }
  };
  const openStats = () => {
    if (statsOpen) return;
    window.history.pushState({ page: 'stats' }, '', '#stats');
    setStatsOpen(true);
  };
  const closeStats = () => {
    setStatsOpen(false);
    if (location.hash === '#stats') {
      if (window.history.state?.page === 'stats') window.history.back();
      else window.history.replaceState(null, '', location.pathname + location.search);
    }
  };
  const persist = (session: typeof s, confidence?: Confidence) => {
    savedProgress.current = progressSignature(session);
    return store.save(session, sessionMode.current, confidence).then(refreshStats);
  };
  const saveCurrent = (outcome: 'skipped' | 'abandoned') => {
    if (ready && !controller.snapshot.session.solved) {
      controller.end(outcome);
      void persist(controller.snapshot.session);
    }
  };
  const load = (p: PuzzleDefinition, mode: string = settings.mode) => {
    setExactLoad(undefined);
    sessionMode.current = mode;
    controller.load(p);
    setReady(true);
    setFlipped(false);
  };
  const next = async () => {
    const token = beginNavigation();
    if (ready && !s.solved) return;
    let p = queue.take();
    if (!p) {
      setReady(false);
      await queue.fill();
      if (token !== navigation.current) return;
      p = queue.take();
    }
    if (p) load(p);
  };
  // Optional: a clean solve moves on by itself; any assistance waits for Next.
  const autoNext = useRef(next);
  autoNext.current = next;
  const clean = s.solved && !s.continuation && !s.mistakes.length && !s.hintsUsed && !s.revealed;
  const autoNextArmed = settings.autoNext && clean && ready && !home && !statsOpen && !sheet;
  useEffect(() => {
    if (!autoNextArmed) return;
    const timer = window.setTimeout(() => void autoNext.current(), AUTO_NEXT_MS);
    return () => window.clearTimeout(timer);
  }, [autoNextArmed, s.generation]);
  const attempt = (uci: string) => {
    const before = controller.snapshot;
    const mistakes = before.session.mistakes.length;
    const firstMove = !madeMove.current;
    const ok = controller.attempt(uci);
    if (ok) {
      madeMove.current = true;
      setHasMoved(true);
      void audio.unlock();
      // The subscription runs before the first legal move unlocks audio.
      const move = controller.snapshot.session.lastMove;
      if (firstMove && move) audio.play(moveSound(move));
    }
    const after = controller.snapshot.session;
    if (ok && before.status === 'user-turn')
      haptics.fire(
        after.mistakes.length > mistakes ? 'wrong' : after.solved ? 'solved' : 'correct',
      );
    if (ok && !reduced && before.status === 'user-turn' && after.lastMove)
      setMoveCue({
        id: ++cueId.current,
        generation: after.generation,
        to: after.lastMove.to,
        correct: after.index > before.session.index,
      });
    // Enqueue gesture outcomes immediately; the effect skips the matching snapshot.
    if (controller.snapshot.session.mistakes.length > mistakes)
      void persist(controller.snapshot.session);
    return ok;
  };
  const hint = () => {
    const before = controller.snapshot;
    controller.hint();
    if (controller.snapshot !== before) void persist(controller.snapshot.session);
  };
  const replay = () => {
    if (['incorrect', 'mistake-demo', 'mistake-analyzing'].includes(controller.snapshot.status)) {
      controller.retry();
      return;
    }
    saveCurrent('abandoned');
    controller.replay();
    setReady(true);
  };
  const updateSettings = (patch: Partial<Settings>) => setSettings((v) => ({ ...v, ...patch }));
  const testSound = async () => {
    if (!madeMove.current) return;
    const volume = settings.volume || defaults.volume;
    updateSettings({ muted: false, volume });
    audio.settings(false, volume, settings.overOtherAudio);
    setSoundTest('Loading sound…');
    setSoundTest(
      (await audio.preview())
        ? 'Test sound played.'
        : 'Audio could not start. Tap Test sound to retry.',
    );
  };
  const updateFilters = (patch: Partial<Settings>) => setFilterDraft((v) => ({ ...v, ...patch }));
  const applyFilters = (theme = filterDraft.theme) =>
    startPractice({
      ...settings,
      mode: filterDraft.mode,
      difficulty: filterDraft.difficulty,
      theme,
      ratingMin: filterDraft.ratingMin,
      ratingMax: filterDraft.ratingMax,
      quality: settings.quality,
      // Start Adaptive at the current estimate, not the starting guess.
      adaptiveRange: strength === undefined ? settings.adaptiveRange : adaptiveRange(strength),
    });
  const startPractice = async (applied: Settings) => {
    const token = beginNavigation();
    if (ready) saveCurrent('abandoned');
    setReady(false);
    setHome(false);
    setStatsOpen(false);
    closeSheet();
    setSettings(applied);
    await queue.initialize(applied);
    if (token !== navigation.current) return;
    const p = queue.take();
    if (p) load(p, applied.mode);
  };
  useEffect(() => {
    controller.animateSetup = settings.animateSetup && !reduced;
  }, [controller, settings.animateSetup, reduced]);
  useEffect(() => {
    // R5: the hidden adaptive range follows the strength estimate. Small moves wait.
    if (strength === undefined || settings.difficulty !== 'adaptive') return;
    const range = adaptiveRange(strength);
    if (settings.adaptiveRange && Math.abs(settings.adaptiveRange[0] - range[0]) < 50) return;
    const tuned = { ...settings, adaptiveRange: range };
    setSettings(tuned);
    queue.retune(tuned);
  }, [strength, settings, queue]);
  const exact = async (id: string) => {
    const token = beginNavigation();
    const abort = new AbortController();
    exactAbort.current = abort;
    setExactLoad({ id, failed: false });
    saveCurrent('abandoned');
    setReady(false);
    setHome(false);
    closeStats();
    closeSheet();
    try {
      const p = await queue.exact(id, abort.signal);
      if (token === navigation.current) {
        void store.markSeen(p);
        load(p);
      }
    } catch {
      if (token === navigation.current) setExactLoad({ id, failed: true });
    }
  };
  const exportData = async () => {
    const data = await store.export(),
      url = URL.createObjectURL(
        new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }),
      ),
      a = document.createElement('a');
    a.href = url;
    a.download = `aftermove-progress-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const shortcut = useRef<(e: KeyboardEvent) => void>(() => {});
  shortcut.current = (e) => {
    if (
      e.ctrlKey ||
      e.metaKey ||
      e.altKey ||
      e.repeat ||
      (e.target instanceof HTMLElement &&
        (e.target.matches('input,textarea,select') || e.target.isContentEditable))
    )
      return;
    if (statsOpen && !sheet) {
      if (e.key === 'Escape') closeStats();
      return;
    }
    if (home && !sheet) return;
    if (sheet) {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeSheet();
      }
      return;
    }
    if (e.target instanceof HTMLElement && e.target.closest('dialog, [role="dialog"]')) return;
    switch (e.key.toLowerCase()) {
      case 'n':
        if (ready && s.solved) void next();
        break;
      case 'h':
        if (ready && !s.solved) {
          if (reviewingMistake) replay();
          hint();
        }
        break;
      case 'r':
        if (ready) replay();
        break;
      case 'f':
        controller.startContinuation(
          s.offer?.kind === 'mate'
            ? 'finish-mate'
            : s.offer?.kind === 'winning'
              ? 'convert'
              : 'play-out',
        );
        break;
      case 'p':
        controller.startContinuation('play-out');
        break;
      case 'escape':
        controller.exitContinuation();
        break;
      case 'm':
        updateSettings({ muted: !settings.muted });
        break;
      case 'b':
        setFlipped((v) => !v);
        break;
      default:
        return;
    }
    e.preventDefault();
  };
  useEffect(() => {
    const handler = (e: KeyboardEvent) => shortcut.current(e);
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);
  const modeLabel = {
    mixed: 'Endless mix',
    theme: 'Theme practice',
    review: 'Review',
    favorites: 'Favorites',
  }[settings.mode];

  const active = ready && ['user-turn', 'continuation-user-turn'].includes(state.status);
  const reviewingMistake = state.status === 'incorrect' || state.status.startsWith('mistake-');
  const showThemes = settings.themesWhileSolving || s.solved;
  const knownTheme = sessionMode.current === 'theme' ? settings.theme : undefined;
  const lineContinues = !!s.refutation && s.refutation.index < s.refutation.moves.length;
  const hintAction = (
    <button
      className="hint-action"
      disabled={(!active && !reviewingMistake) || s.solved}
      onClick={() => {
        if (reviewingMistake) replay();
        hint();
      }}
      aria-label={
        s.hintLevel === 2
          ? 'Reveal next move'
          : s.hintLevel === 1
            ? 'Show destination'
            : 'A little hint'
      }
    >
      <Icon name="hint" />
      <span>{s.hintLevel === 2 ? 'Reveal' : s.hintLevel === 1 ? 'Destination' : 'Hint'}</span>
    </button>
  );
  const practiceOptions = (
    <PracticeSetup
      draft={filterDraft}
      update={updateFilters}
      step={practiceStep}
      setStep={setPracticeStep}
      start={() => void applyFilters()}
      review={review}
    />
  );
  return (
    <div
      className={`app-shell ${statsOpen ? 'stats-shell' : home ? 'home-shell' : ''}`}
      data-reduced={reduced}
    >
      <header className="topbar">
        <a
          className="brand"
          href="/"
          aria-label="Chess Puzzles home"
          onClick={(event) => {
            event.preventDefault();
            beginNavigation();
            setFilterDraft(settings);
            setPracticeStep('difficulty');
            closeStats();
            setHome(true);
          }}
        >
          <img src="/design/brand/app-icon.svg" alt="" width="32" height="32" />
          <span className="brand-name">
            Chess <span>Puzzles</span>
          </span>
        </a>
        <nav aria-label="Practice controls">
          <button className="icon-button" aria-label="Stats" title="Stats" onClick={openStats}>
            <Icon name="stats" />
          </button>
          <button
            className="icon-button"
            aria-label={settings.muted ? 'Unmute sounds' : 'Mute sounds'}
            title={settings.muted ? 'Unmute sounds' : 'Mute sounds'}
            onClick={() => updateSettings({ muted: !settings.muted })}
          >
            <Icon name={settings.muted ? 'muted' : 'sound'} />
          </button>
          <button
            className="icon-button"
            aria-label="Settings"
            title="Settings"
            onClick={() => openSheet('settings')}
          >
            <Icon name="settings" />
          </button>
        </nav>
      </header>
      {notice && (
        <div className="notice" role="status">
          {notice}
          <button aria-label="Dismiss notice" onClick={() => setNotice('')}>
            <Icon name="close" />
          </button>
        </div>
      )}
      {statsOpen ? (
        <StatsPage
          history={history}
          recent={recent ?? null}
          stats={stats}
          allTime={allTime}
          reduced={reduced}
          back={closeStats}
          resume={
            ready
              ? () => {
                  closeStats();
                  setHome(false);
                }
              : undefined
          }
          exact={(id) => void exact(id)}
          choose={() => {
            beginNavigation();
            closeStats();
            setFilterDraft(settings);
            setPracticeStep('difficulty');
            setHome(true);
          }}
          scores={ready ? { moves: scoreMoves, lastMove: s.lastMove } : undefined}
        />
      ) : home ? (
        <main className="home-menu">
          <section className="practice-builder" aria-label="Build your practice">
            {practiceStep === 'difficulty' && (
              <div className="practice-title">
                <h1>Choose your practice</h1>
                {ready && (
                  <button
                    className="resume-button"
                    onClick={() => setHome(false)}
                    aria-label="Resume current puzzle"
                  >
                    Resume <Icon name="arrow" />
                  </button>
                )}
              </div>
            )}
            {practiceStep === 'difficulty' && <TodayLine stats={allTime} />}

            {practiceOptions}
          </section>
        </main>
      ) : (
        <main className={`workbench ${notebook ? 'notebook-layout' : 'stacked-layout'}`}>
          <section className="board-column" aria-label="Puzzle board">
            <div className="board-heading">
              {ready && !notebook && <LastMove move={s.lastMove} />}
              {notebook && (
                <button className="mode-button" onClick={() => openSheet('filters')}>
                  {modeLabel} <Icon name="chevron" />
                </button>
              )}
            </div>
            {!ready ? (
              <BoardSkeleton reduced={reduced} />
            ) : (
              <MoveFeedback
                cue={moveCue?.generation === s.generation ? moveCue : undefined}
                orientation={(s.puzzle.solverColor === 'w') !== flipped ? 'white' : 'black'}
                reduced={reduced}
              >
                <BoardAdapter
                  key={s.generation}
                  fen={s.fen}
                  orientation={(s.puzzle.solverColor === 'w') !== flipped ? 'white' : 'black'}
                  enabled={active}
                  legalMoves={rules.legal()}
                  checkSquare={rules.checkSquare}
                  lastMove={s.lastMove}
                  moveTone={
                    reviewingMistake
                      ? state.status === 'mistake-demo'
                        ? 'defense'
                        : 'mistake'
                      : 'normal'
                  }
                  hint={s.puzzle.solutionMoves[s.index]}
                  hintLevel={s.hintLevel}
                  reduced={reduced}
                  animationMs={s.index === 0 && !s.mistakes.length ? 260 : undefined}
                  onHold={() => haptics.fire('mark')}
                  pen={pen}
                  holdToDraw={settings.holdToDraw}
                  clearToken={clearToken}
                  onAnnotations={setAnnotations}
                  coordinates={settings.coordinates}
                  onMove={attempt}
                />
              </MoveFeedback>
            )}
            <div className="board-footer">
              {!notebook && ready && (
                <PuzzleFacts
                  key={s.puzzle.id}
                  puzzle={s.puzzle}
                  compact
                  showThemes={showThemes}
                  knownTheme={knownTheme}
                />
              )}
              {ready && (
                <button
                  className="board-flip board-pen"
                  aria-pressed={pen}
                  aria-label={pen ? 'Pen on: tap to move pieces again' : 'Pen: draw on the board'}
                  title={pen ? 'Drawing. Tap to move pieces again' : 'Draw highlights and arrows'}
                  onClick={() => setPen((on) => !on)}
                >
                  <Icon name="pen" /> <span>{pen ? 'Drawing' : 'Pen'}</span>
                </button>
              )}
              {ready && annotations > 0 && (
                <button
                  className="board-flip board-clear"
                  aria-label="Clear drawings"
                  title="Clear highlights and arrows"
                  onClick={() => setClearToken((n) => n + 1)}
                >
                  <Icon name="close" /> <span>Clear</span>
                </button>
              )}
              <button
                className="board-flip"
                aria-label="Flip board"
                onClick={() => setFlipped(!flipped)}
              >
                <Icon name="flip" /> <span>Flip board</span>
              </button>
            </div>
          </section>
          {!notebook && (
            <div className="mobile-summary">
              {ready && <SessionStrip stats={stats} reduced={reduced} />}
            </div>
          )}
          <aside
            className={`context-panel ${s.solved ? 'is-solved' : ''}`}
            aria-label="Puzzle controls"
          >
            <div className="notebook-content">
              <TurnPrompt
                state={state}
                ready={ready}
                loadingLabel={
                  exactLoad
                    ? exactLoad.failed
                      ? 'That saved puzzle could not be loaded. Retry when connected.'
                      : `Loading saved puzzle #${exactLoad.id}…`
                    : queue.label
                }
                streak={stats.streak}
              />
              {notebook && ready && (
                <>
                  <PuzzleFacts
                    key={s.puzzle.id}
                    puzzle={s.puzzle}
                    showThemes={showThemes}
                    knownTheme={knownTheme}
                  />
                  <Scoresheet moves={scoreMoves} lastMove={s.lastMove} />
                  <SessionStrip stats={stats} reduced={reduced} />
                </>
              )}
            </div>
            <nav
              className={`panel-actions ${ready && !s.solved && !reviewingMistake ? 'primary-empty' : ''}`}
              aria-label="Puzzle actions"
            >
              {!ready ? (
                <>
                  <button onClick={() => openSheet('filters')}>Change filters</button>
                  <button
                    className="primary"
                    disabled={!!exactLoad && !exactLoad.failed}
                    onClick={() => {
                      if (exactLoad) {
                        void exact(exactLoad.id);
                        return;
                      }
                      if (queue.exhausted && settings.mode === 'review') {
                        beginNavigation();
                        // Review is finished: back to choosing a practice.
                        setFilterDraft({ ...settings, mode: 'mixed' });
                        setPracticeStep('difficulty');
                        setHome(true);
                      } else if (queue.exhausted) {
                        const token = beginNavigation();
                        void queue.practiceAgain().then(() => {
                          if (token !== navigation.current) return;
                          const p = queue.take();
                          if (p) load(p);
                        });
                      } else void next();
                    }}
                  >
                    {exactLoad
                      ? exactLoad.failed
                        ? 'Retry saved puzzle'
                        : 'Loading saved puzzle…'
                      : queue.exhausted
                        ? settings.mode === 'review'
                          ? 'Choose practice'
                          : 'Practice again'
                        : 'Retry library'}
                  </button>
                  <span aria-hidden="true" />
                  <span aria-hidden="true" />
                </>
              ) : (
                <>
                  {notebook && hintAction}
                  <button
                    className="replay-action"
                    aria-label="Replay puzzle"
                    onClick={() => {
                      saveCurrent('abandoned');
                      controller.replay();
                    }}
                  >
                    <Icon name="replay" />
                    <span>Replay</span>
                  </button>
                  {!notebook && hintAction}
                  <button
                    className="review-action"
                    aria-label={lineContinues ? 'Continue engine line' : 'Review position'}
                    onClick={() =>
                      lineContinues ? controller.stepRefutation() : openSheet('review')
                    }
                  >
                    <Icon name={lineContinues ? 'rise' : 'history'} />
                    <span>{lineContinues ? 'Continue' : 'Review'}</span>
                  </button>
                  <span className="primary-slot">
                    {(s.solved || reviewingMistake) && (
                      <button
                        className={`advance-button ${s.solved ? 'primary' : 'retry-action'}`}
                        data-testid="advance-puzzle"
                        ref={nextButton}
                        onClick={() => {
                          if (s.solved) void next();
                          else replay();
                        }}
                        title={s.solved ? 'Next puzzle (N)' : 'Try again (R)'}
                        aria-label={s.solved ? 'Next puzzle' : 'Try again'}
                      >
                        <Icon name={s.solved ? 'arrow' : 'replay'} />
                        <span>{s.solved ? 'Next' : 'Try again'}</span>
                      </button>
                    )}
                  </span>
                </>
              )}
            </nav>
          </aside>
        </main>
      )}
      <footer className="site-footer">
        <span>{home ? 'Your pace. Your practice.' : queue.label}</span>
        <button onClick={() => openSheet('licenses')}>Credits & licenses</button>
      </footer>
      {sheet && (
        <Sheet
          title={
            {
              review: 'Review position',
              settings: 'Settings',
              filters: 'Choose your practice',
              details: 'The position, understood',
              licenses: 'Made for the love of chess',
              reset: 'Reset local progress?',
            }[sheet] ?? sheet
          }
          close={closeSheet}
        >
          {sheet === 'review' && (
            <div className="review-actions">
              <p className="muted">
                {!s.solved
                  ? 'Retry the position or see its details.'
                  : s.continuation && !s.result
                    ? 'Your tactic is saved. Keep playing or leave the position.'
                    : state.status === 'continuation-classifying'
                      ? 'Checking the position…'
                      : s.offer?.kind === 'mate'
                        ? `Mate in ${s.offer.moves}. Continue against the best defense.`
                        : s.offer?.kind === 'winning'
                          ? 'Practice converting your advantage.'
                          : s.offer?.kind === 'unavailable'
                            ? 'Engine unavailable. Your tactic still counts.'
                            : 'Replay the tactic or keep playing this position.'}
              </p>
              {s.continuation && state.status !== 'game-over' && (
                <div className="utility-row">
                  <button
                    onClick={() => {
                      controller.exitContinuation();
                      closeSheet();
                    }}
                  >
                    Exit position
                  </button>
                  <button
                    onClick={() => {
                      controller.exitContinuation('resigned');
                      closeSheet();
                    }}
                  >
                    Resign
                  </button>
                </div>
              )}
              {s.solved && !s.result && !s.continuation && (
                <>
                  {(s.offer?.kind === 'mate' || s.offer?.kind === 'winning') && (
                    <button
                      className="continuation-button"
                      onClick={() => {
                        controller.startContinuation(
                          s.offer?.kind === 'mate' ? 'finish-mate' : 'convert',
                        );
                        closeSheet();
                      }}
                    >
                      {s.offer.kind === 'mate' ? 'Finish the Mate' : 'Convert the Advantage'}{' '}
                      <Icon name="rise" />
                    </button>
                  )}
                  <button
                    disabled={s.offer?.kind === 'unavailable'}
                    onClick={() => {
                      controller.startContinuation('play-out');
                      closeSheet();
                    }}
                  >
                    Play It Out
                  </button>
                </>
              )}
              <button
                onClick={() => {
                  replay();
                  closeSheet();
                }}
              >
                Replay tactic
              </button>
              <div className="utility-row">
                <button onClick={() => replaceSheet('details')}>Details</button>
              </div>
            </div>
          )}
          {sheet === 'settings' && (
            <div className="settings-grid">
              <details className="advanced-settings">
                <summary>Puzzle library options</summary>
                <label>
                  Quality pool
                  <select
                    value={settings.quality}
                    onChange={(e) =>
                      updateSettings({ quality: e.target.value as Settings['quality'] })
                    }
                  >
                    <option value="curated">Preferred &amp; standard</option>
                    <option value="all">All validated puzzles</option>
                  </select>
                </label>
                <p className="muted">Applies when you start your next practice session.</p>
              </details>
              <label className="toggle">
                Sounds
                <input
                  type="checkbox"
                  checked={!settings.muted}
                  onChange={(e) => updateSettings({ muted: !e.target.checked })}
                />
              </label>
              <label>
                Master volume <span>{Math.round(settings.volume * 100)}%</span>
                <input
                  aria-label="Master volume"
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={settings.volume}
                  onChange={(e) => updateSettings({ volume: Number(e.target.value) })}
                />
              </label>
              <div>
                <button
                  disabled={!hasMoved || soundTest === 'Loading sound…'}
                  onClick={() => {
                    void testSound();
                  }}
                >
                  <Icon name="sound" /> Test sound
                </button>
                {!hasMoved && <p className="muted">Make a move to enable the sound test.</p>}
                {soundTest && (
                  <p className="muted" role="status">
                    {soundTest}
                  </p>
                )}
              </div>
              <label className="toggle">
                Right/wrong sounds
                <input
                  type="checkbox"
                  checked={settings.feedbackSounds}
                  onChange={(e) => updateSettings({ feedbackSounds: e.target.checked })}
                />
              </label>
              <label className="toggle">
                Haptics
                <input
                  type="checkbox"
                  checked={settings.haptics}
                  onChange={(e) => updateSettings({ haptics: e.target.checked })}
                />
              </label>
              {settings.haptics && haptics.mode === 'switch' && (
                <p className="muted">On iPhone, haptics need iOS 18 or later.</p>
              )}
              <label className="toggle">
                Play sounds over other audio
                <input
                  type="checkbox"
                  checked={settings.overOtherAudio}
                  onChange={(e) => updateSettings({ overOtherAudio: e.target.checked })}
                />
              </label>
              <label className="toggle">
                Board coordinates
                <input
                  type="checkbox"
                  checked={settings.coordinates}
                  onChange={(e) => updateSettings({ coordinates: e.target.checked })}
                />
              </label>
              <label className="toggle">
                Show themes while solving
                <input
                  type="checkbox"
                  checked={settings.themesWhileSolving}
                  onChange={(e) => updateSettings({ themesWhileSolving: e.target.checked })}
                />
              </label>
              <label className="toggle">
                Auto-next after a clean solve
                <input
                  type="checkbox"
                  checked={settings.autoNext}
                  onChange={(e) => updateSettings({ autoNext: e.target.checked })}
                />
              </label>
              <label className="toggle">
                Press and hold to draw
                <input
                  type="checkbox"
                  checked={settings.holdToDraw}
                  onChange={(e) => updateSettings({ holdToDraw: e.target.checked })}
                />
              </label>
              <label className="toggle">
                Show the opponent's move first
                <input
                  type="checkbox"
                  checked={settings.animateSetup}
                  onChange={(e) => updateSettings({ animateSetup: e.target.checked })}
                />
              </label>
              <label className="toggle">
                Reduce motion
                <input
                  type="checkbox"
                  checked={settings.reduced}
                  onChange={(e) => updateSettings({ reduced: e.target.checked })}
                />
              </label>
              <label>
                Engine opponent
                <select
                  value={settings.engineProfile}
                  onChange={(e) =>
                    updateSettings({ engineProfile: e.target.value as Settings['engineProfile'] })
                  }
                >
                  <option value="casual">Casual</option>
                  <option value="strong">Strong</option>
                  <option value="best">Best defense</option>
                </select>
              </label>
              <p className="muted">Progress stays in this browser. No account, no tracking.</p>
              <p className="muted">{queue.label}</p>
              <button onClick={() => replaceSheet('licenses')}>Credits & licenses</button>
              <button
                onClick={() => {
                  void exportData();
                }}
              >
                Export personal data
              </button>
              <button className="danger" onClick={() => replaceSheet('reset')}>
                Reset local progress
              </button>
              <p className="shortcut-guide">
                N next after solving · R retry/replay · H hint · M mute · B flip
                <br />
                Arrow keys and Enter move between board squares.
                <br />
                Select a piece to see legal moves. Hold right-click and drag to draw arrows.
                Drawings stay until Clear or a move; draw the same arrow again to remove it.
              </p>
            </div>
          )}
          {sheet === 'filters' && practiceOptions}
          {sheet === 'details' && (
            <>
              <p>
                {s.puzzle.rating} difficulty · #{s.puzzle.id}
              </p>
              {s.solved ? (
                <>
                  <div className="theme-tags">
                    {visibleThemes.map((t) => (
                      <span key={t}>{themeLabel(t)}</span>
                    ))}
                  </div>
                  <p>How did it feel?</p>
                  <div className="confidence">
                    {(
                      [
                        ['whole-line', 'Saw the whole line'],
                        ['idea', 'Saw the idea'],
                        ['lucky', 'Got lucky'],
                      ] as [Confidence, string][]
                    ).map(([value, label]) => (
                      <button
                        key={value}
                        onClick={() => {
                          void persist(s, value);
                          setNotice('Confidence saved.');
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <p className="muted">
                    {s.mistakes.length} wrong attempts · {s.hintsUsed} hints
                  </p>
                </>
              ) : (
                <p>Solve the tactic to reveal its themes.</p>
              )}
              {s.puzzle.sourceGameUrl && (
                <p>
                  <a href={s.puzzle.sourceGameUrl} target="_blank" rel="noreferrer">
                    View the source game on Lichess <Icon name="external" />
                  </a>
                </p>
              )}
            </>
          )}
          {sheet === 'reset' && (
            <>
              <p>
                This clears attempts, favorites, seen puzzles, and cached positions from this
                browser. Export a copy first if you want to keep them.
              </p>
              <div className="utility-row">
                <button onClick={closeSheet}>Keep my progress</button>
                <button
                  className="danger"
                  onClick={() => {
                    beginNavigation();
                    setReady(false);
                    setHome(true);
                    setStatsOpen(false);
                    // Invalidate engine/timer work and retire the old attempt before clearing.
                    controller.load(fixtures[0]);
                    void store.reset().then(() => {
                      void refreshStats();
                      void queue.resetProgress();
                      setNotice('Local progress cleared.');
                      setSheet(undefined);
                      window.history.replaceState(null, '', location.pathname + location.search);
                    });
                  }}
                >
                  Clear local progress
                </button>
              </div>
            </>
          )}
          {sheet === 'licenses' && (
            <div className="license-copy">
              <a className="design-credit" href="/design/" target="_blank" rel="noreferrer">
                <img src="/design/brand/mark.svg" alt="" width="36" height="36" />
                <span>Explore the original Studio collection</span>
                <Icon name="external" />
              </a>
              <p>
                Chess Puzzles' original software, Studio artwork, and feedback tones use
                GPL-3.0-or-later.
              </p>
              <p>
                Board, piece images, and move sounds:{' '}
                <a href="https://www.chess.com" target="_blank" rel="noreferrer">
                  Chess.com
                </a>
                . These third-party assets are separate from Chess Puzzles' GPL license.{' '}
                <a href="/vendor/chesscom/manifest.json" target="_blank" rel="noreferrer">
                  Asset sources
                </a>
              </p>
              <p>
                Puzzle records:{' '}
                <a href="https://database.lichess.org/#puzzles" target="_blank" rel="noreferrer">
                  Lichess Open Database
                </a>
                , CC0. React: MIT. react-chessboard: MIT. chess.js: BSD-2-Clause. idb: ISC.
              </p>
              <p>
                Stockfish.js 19: GPLv3, © 2026 Chess.com, LLC and Stockfish contributors. The
                open-source engine is used under its license.
              </p>
              <p>
                <a href="/licenses/THIRD_PARTY_NOTICES.txt" target="_blank">
                  Full notices
                </a>{' '}
                ·{' '}
                <a href="/licenses/GPL-3.0.txt" target="_blank">
                  GPL text
                </a>{' '}
                · <a href="/source/aftermove-source.tar.gz">Application source</a> ·{' '}
                <a href="/stockfish/19-lite-single/SOURCE.md" target="_blank">
                  Engine source & build instructions
                </a>
              </p>
            </div>
          )}
        </Sheet>
      )}
    </div>
  );
}
